import { AlertTriangle, AlertCircle, Info } from 'lucide-react';
import { useDashboard } from '@/hooks/useDashboard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import type { DashboardAction } from '@/lib/types';
import { useIsTV } from '@/hooks/useDevice';
import { cn } from '@/lib/utils';

const sevConfig: Record<DashboardAction['severity'], { variant: 'destructive' | 'warning' | 'secondary'; icon: React.ComponentType<{ className?: string }>; tone: string }> = {
  high: { variant: 'destructive', icon: AlertTriangle, tone: 'border-destructive/40 bg-destructive/5' },
  medium: { variant: 'warning', icon: AlertCircle, tone: 'border-warning/40 bg-warning/5' },
  low: { variant: 'secondary', icon: Info, tone: 'border-border' },
};

export function Actions() {
  const { data, isLoading } = useDashboard();
  const isTV = useIsTV();

  if (isLoading) return <Skeleton className="h-96" />;
  if (!data) return null;

  const groups: Record<DashboardAction['severity'], DashboardAction[]> = { high: [], medium: [], low: [] };
  data.actions.forEach((a) => groups[a.severity].push(a));

  const Section = ({ sev }: { sev: DashboardAction['severity'] }) => {
    const items = groups[sev];
    if (items.length === 0) return null;
    const cfg = sevConfig[sev];
    const Icon = cfg.icon;
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className={cn('flex items-center gap-2', isTV ? 'text-base' : 'text-sm')}>
            <Icon className="h-4 w-4" />
            <span className="capitalize">{sev === 'high' ? 'Urgent' : sev === 'medium' ? 'Important' : 'À noter'}</span>
            <Badge variant={cfg.variant}>{items.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {items.map((a, i) => (
            <div key={i} className={cn('rounded-lg border p-3', cfg.tone)}>
              <div className={cn('font-semibold', isTV ? 'text-base' : 'text-sm')}>{a.title}</div>
              {(a.detail || a.meta) && <div className={cn('mt-1 text-muted-foreground', isTV ? 'text-sm' : 'text-xs')}>{a.detail || a.meta}</div>}
              {a.explanation && !isTV && <div className="mt-2 text-xs text-muted-foreground/80">{a.explanation}</div>}
            </div>
          ))}
        </CardContent>
      </Card>
    );
  };

  if (data.actions.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center gap-3 py-12 text-muted-foreground">
          <AlertTriangle className="h-8 w-8 text-success" />
          Aucune action urgente. Tout va bien.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <Section sev="high" />
      <Section sev="medium" />
      <Section sev="low" />
    </div>
  );
}
